import { useState, useRef } from 'react'
import { message } from 'antd'
import _ from 'lodash'
import {
  generateVideoDeepAI,
  generateLumaVideo,
  getTaskStatus,
  assembleScenesWithAudio
} from '../../../../../services/api/aiVideoGeneration'

const getFormattedPrompt = (prompt, formData = {}) => {
  const selectedCharacters = formData?.selectedCharacter || []
  const formattedPrompt = selectedCharacters.reduce((acc, char, index) => {
    const placeholder = `__character${index + 1}__`
    return acc.replace(
      new RegExp(placeholder, 'g'),
      char.current.replace(/__/g, '')
    )
  }, prompt)
  return formattedPrompt
}

const getAudioUrlForShot = (scene, shotKey) => {
  if (!scene?.segment_timings || !shotKey) return null
  const timing = scene.segment_timings.find(
    t => t.shot_id === shotKey && t.type === 'voice' && t.audio_url
  )
  return timing?.audio_url || null
}

const getLumaPayload = (videoData, negative_prompt = '', formData, shot) => {
  return {
    prompt: getFormattedPrompt(videoData.prompt, formData),
    img_url: videoData.image_url,
    duration: videoData.duration,
    video_id: videoData.video_id,
    audio_url: videoData.audio_url,
    shot: shot || null,
    character_map: formData?.characterMap || null,
    outfit_map: formData?.outfitMap || null,
    wardrobe: formData?.wardrobe || null,
    outfits_reference: formData?.outfits_reference || null,
    character_voices: formData?.characterVoices || null
  }
}

export const useVideoGeneration = ({
  scenes,
  setScenes,
  formData,
  videoProvider,
  videoId
}) => {
  const [videoGenerating, setVideoGenerating] = useState([])
  const [
    generatingVideoForAllScenes,
    setGeneratingVideoForAllScenes
  ] = useState(false)

  const updateShotDataRef = useRef(
    _.debounce((sceneId, shotId, updatesOrKey, value) => {
      setScenes(prevScenes =>
        prevScenes.map(scene => {
          if (scene.id === sceneId) {
            return {
              ...scene,
              shots: scene.shots.map(shot => {
                if (shot.id === shotId) {
                  if (
                    typeof updatesOrKey === 'object' &&
                    updatesOrKey !== null
                  ) {
                    return { ...shot, ...updatesOrKey }
                  }
                  return { ...shot, [updatesOrKey]: value }
                }
                return shot
              })
            }
          }
          return scene
        })
      )
    }, 500)
  )

  const waitForVideoTaskSuccess = async (taskId, onProgress) => {
    try {
      if (!taskId) return null
      const delayMs = 10000

      while (true) {
        try {
          const statusResponse = await getTaskStatus(taskId)
          const data = statusResponse?.data || null
          const status = data?.status || null

          if (data?.progress && onProgress) {
            onProgress(data.progress)
          }

          if (status === 'success') {
            return data?.output || ''
          }
          if (status === 'failed' || status === 'error') {
            return null
          }
          if (
            status === 'pending' &&
            (data?.progress === '100%' || data?.progress === 100)
          ) {
            continue
          }
        } catch (err) {
          console.error('Error fetching video task status, retrying:', err)
          return null
        }
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
    } catch (err) {
      return null
    }
  }

  const handleRegenerateVideo = async (sceneId, shotId) => {
    try {
      setVideoGenerating(prevStatus => [
        ...(prevStatus || []),
        { sceneId, shotId }
      ])

      const scene = scenes.find(s => s.id === sceneId)
      const shot = scene.shots?.find(sh => sh.id === shotId)

      setScenes(prevScenes =>
        prevScenes.map(s => {
          if (s.id === sceneId) {
            return {
              ...s,
              shots: s.shots.map(sh =>
                sh.id === shotId ? { ...sh, progress: 0 } : sh
              )
            }
          }
          return s
        })
      )

      const videoData = {
        prompt: shot.video_prompt,
        image_url: shot.imageSrc,
        audio_url: getAudioUrlForShot(
          scene,
          shot.key || `shot ${shotId.split('-').pop()}`
        ),
        ...(videoProvider !== 'DeepAI' && { duration: shot.video_duration }),
        video_id: videoId,
        shot: shot || null,
        character_map: formData?.characterMap || null,
        outfit_map: formData?.outfitMap || null,
        wardrobe: formData?.wardrobe || null,
        outfits_reference: formData?.outfits_reference || null
      }

      const request =
        videoProvider === 'DeepAI' ? generateVideoDeepAI : generateLumaVideo
      const payload =
        videoProvider === 'DeepAI'
          ? videoData
          : getLumaPayload(videoData, shot.negative_prompt, formData, shot)

      console.log({
        regenerateVideo: true,
        payload,
        request
      })

      if (shot.shot_dialogue_audio_link) {
        payload.audio_url = shot.shot_dialogue_audio_link
      }
      const response = await request(payload)
      const taskId = response?.data?.task_id
      const videoLink = await waitForVideoTaskSuccess(taskId, progress => {
        const progressVal = parseInt(progress) || 0
        updateShotDataRef.current(sceneId, shotId, 'progress', progressVal)
      })

      setScenes(prevScenes =>
        prevScenes.map(s => {
          if (s.id === sceneId) {
            return {
              ...s,
              shots: s.shots.map(sh =>
                sh.id === shotId
                  ? {
                      ...sh,
                      progress: 100,
                      ...(videoLink
                        ? {
                            vidSrc: videoLink,
                            errorGeneratingVideo: false,
                            video_duration: shot.video_duration,
                            videos_history: [
                              ...(sh.videos_history || []),
                              {
                                url: videoLink,
                                timestamp: Date.now(),
                                duration_at_generation: shot.video_duration,
                                source_image: sh.imageSrc
                              }
                            ]
                          }
                        : { errorGeneratingVideo: true })
                    }
                  : sh
              )
            }
          }
          return s
        })
      )

      if (videoLink) {
        message.success(`Video generated successfully!`)
      } else {
        throw new Error('Video generation failed')
      }
    } catch (error) {
      message.error('Error regenerating video: ' + error.message)
      console.error('Error regenerating video:', error)
    } finally {
      setVideoGenerating(prevStatus =>
        (prevStatus || []).filter(
          status => !(status.sceneId === sceneId && status.shotId === shotId)
        )
      )
    }
  }

  const handleGenerateFullVideo = async (targetScenes = null) => {
    try {
      if (!targetScenes) {
        setGeneratingVideoForAllScenes(true)
      }

      const scenesToProcess = targetScenes || scenes
      const affectedSceneIds = new Set()

      const videoQueue = scenesToProcess.flatMap(scene =>
        scene.shots.map(shot => {
          affectedSceneIds.add(scene.id)
          return { scene, shot }
        })
      )

      const promises = videoQueue.map(async ({ scene, shot }) => {
        try {
          const sceneId = scene.id
          const shotId = shot.id

          setVideoGenerating(prevStatus => [
            ...(prevStatus || []),
            { sceneId, shotId }
          ])

          setScenes(prevScenes =>
            prevScenes.map(s => {
              if (s.id === sceneId) {
                return {
                  ...s,
                  shots: s.shots.map(sh =>
                    sh.id === shotId ? { ...sh, progress: 0 } : sh
                  )
                }
              }
              return s
            })
          )

          const videoData = {
            prompt: getFormattedPrompt(shot.video_prompt, formData),
            image_url: shot.imageSrc,
            audio_url: getAudioUrlForShot(
              scene,
              shot.key || `shot ${shotId.split('-').pop()}`
            ),
            ...(videoProvider !== 'DeepAI' && {
              duration: shot.video_duration
            }),
            video_id: videoId,
            shot: shot || null,
            character_map: formData?.characterMap || null,
            outfit_map: formData?.outfitMap || null,
            wardrobe: formData?.wardrobe || null,
            outfits_reference: formData?.outfits_reference || null
          }

          const request =
            videoProvider === 'DeepAI' ? generateVideoDeepAI : generateLumaVideo
          const payload =
            videoProvider === 'DeepAI'
              ? videoData
              : getLumaPayload(videoData, shot.negative_prompt, formData, shot)

          const response = await request(payload)
          const taskId = response?.data?.task_id
          const videoLink = await waitForVideoTaskSuccess(taskId, progress => {
            const progressVal = parseInt(progress) || 0
            updateShotDataRef.current(sceneId, shotId, 'progress', progressVal)
          })

          setScenes(prevScenes =>
            prevScenes.map(s => {
              if (s.id === sceneId) {
                return {
                  ...s,
                  shots: s.shots.map(sh =>
                    sh.id === shotId
                      ? {
                          ...sh,
                          progress: 100,
                          ...(videoLink
                            ? {
                                vidSrc: videoLink,
                                errorGeneratingVideo: false,
                                video_duration: shot.video_duration,
                                videos_history: [
                                  ...(sh.videos_history || []),
                                  {
                                    url: videoLink,
                                    timestamp: Date.now(),
                                    duration_at_generation: shot.video_duration,
                                    source_image: sh.imageSrc
                                  }
                                ]
                              }
                            : { errorGeneratingVideo: true })
                        }
                      : sh
                  )
                }
              }
              return s
            })
          )
        } catch (error) {
          console.error(
            `Error generating video for scene ${scene.id}, shot ${shot.id}:`,
            error
          )
          setScenes(prevScenes =>
            prevScenes.map(s => {
              if (s.id === scene.id) {
                return {
                  ...s,
                  shots: s.shots.map(sh =>
                    sh.id === shot.id
                      ? { ...sh, errorGeneratingVideo: true }
                      : sh
                  )
                }
              }
              return s
            })
          )
        } finally {
          setVideoGenerating(prevStatus =>
            (prevStatus || []).filter(
              status =>
                !(status.sceneId === scene.id && status.shotId === shot.id)
            )
          )
        }
      })

      await Promise.all(promises)
      message.success('Video generation process completed.')
    } catch (error) {
      console.error('Error starting video generation:', error)
      message.error('Error starting video generation: ' + error.message)
    } finally {
      setGeneratingVideoForAllScenes(false)
    }
  }

  const handleRetryFailedVideos = () => {
    const scenesWithFailures = scenes
      .map(scene => {
        const failedShots = scene.shots.filter(shot => {
          const isInQueue = videoGenerating.some(
            v => v.sceneId === scene.id && v.shotId === shot.id
          )
          if (isInQueue) return false
          return (
            shot.errorGeneratingVideo || (!shot.vidSrc && !shot.isGenerating)
          )
        })
        if (failedShots.length > 0) {
          return {
            ...scene,
            shots: failedShots
          }
        }
        return null
      })
      .filter(s => s !== null)

    if (scenesWithFailures.length === 0) {
      message.info('No failed videos to retry.')
      return
    }

    handleGenerateFullVideo(scenesWithFailures)
  }

  return {
    videoGenerating,
    generatingVideoForAllScenes,
    handleGenerateFullVideo,
    handleRegenerateVideo,
    handleRetryFailedVideos,
    updateShotDataRef
  }
}
